/**
 * F3. Рынок: торг. Раз в год в 10–70 лет при фокусе «помощь семье» или
 * при празднике общины. Продавец называет цену (10 % годового дохода),
 * у игрока три предложения. Зона согласия — `bargainZone(relations)`:
 * 5 % плюс по 5 % за каждые 10 очков отношений. Предложение не ниже
 * `floor` = цена · (1 − зона) — сделка, экономия идёт в деньги; ниже
 * `walkAway` = цена · (1 − 2·зоны) — продавец уходит, и ту же вещь
 * берёте дороже: −(цена − floor); три отказа — покупка по названной цене,
 * 0. Итог всегда в пределах ±10 % дохода. «Пропустить» — 0.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome } from '@life/engine';
import type { MinigameDef } from '../types';
import { yearlyIncome } from '../casino/common';
import { nothing } from './common';

export const BARGAIN_MIN_AGE = 10;
export const BARGAIN_MAX_AGE = 70;
/** Цена товара — доля годового дохода. */
export const BARGAIN_PRICE_SHARE = 0.1;
export const BARGAIN_TRIES = 3;
export const BARGAIN_ZONE_BASE = 0.05;
/** +5 % зоны за каждые 10 очков отношений. */
export const BARGAIN_ZONE_PER_POINT = 0.005;

/** Зона согласия продавца, доля цены: 0,05 + 0,005 · отношения. */
export function bargainZone(relations: number): number {
  const r = Number.isFinite(relations) ? Math.max(0, Math.min(100, relations)) : 0;
  return BARGAIN_ZONE_BASE + BARGAIN_ZONE_PER_POINT * r;
}

export interface BargainState {
  price: number;
  zone: number;
  /** Минимум, на который продавец соглашается. */
  floor: number;
  /** Ниже этого продавец уходит. */
  walkAway: number;
  tries: number;
}

export type BargainInput = { offers: number[] };

export const bargain: MinigameDef<BargainInput, BargainState> = {
  id: 'bargain',
  section: 'F',
  triggers: [
    { kind: 'age', min: BARGAIN_MIN_AGE, max: BARGAIN_MAX_AGE, everyYears: 1, focus: 'helpFamily' },
    { kind: 'event', event: 'community.festival' },
  ],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return life.age >= BARGAIN_MIN_AGE && life.age <= BARGAIN_MAX_AGE;
  },
  setup(life: Life, ds: Dataset): BargainState {
    const price = Math.max(0, yearlyIncome(ds, life)) * BARGAIN_PRICE_SHARE;
    const zone = bargainZone(life.stats.relations);
    return { price, zone, floor: price * (1 - zone), walkAway: price * (1 - 2 * zone), tries: BARGAIN_TRIES };
  },
  play(state: BargainState, input: BargainInput, _life: Life): MinigameOutcome {
    const offers = (Array.isArray(input.offers) ? input.offers : []).slice(0, state.tries);
    const saving = state.price - state.floor;
    for (let i = 0; i < offers.length; i += 1) {
      const raw = offers[i];
      const offer = typeof raw === 'number' && Number.isFinite(raw) ? Math.max(0, raw) : 0;
      if (offer >= state.floor) {
        const amount = Math.max(0, state.price - Math.min(offer, state.price));
        return { ...nothing(), moneyDelta: amount, logKey: 'minigame.bargain.deal', logParams: { amount: Math.round(amount), tries: i + 1 } };
      }
      if (offer < state.walkAway) {
        return { ...nothing(), moneyDelta: -saving, logKey: 'minigame.bargain.lost', logParams: { amount: Math.round(saving) } };
      }
    }
    return { ...nothing(), logKey: 'minigame.bargain.none', logParams: { amount: Math.round(state.price) } };
  },
  expected(): MinigameOutcome {
    return nothing();
  },
};
